import { useEffect, useId, useState } from 'react';

import { useT } from '@/i18n';
import { recordShortcut } from '@/lib/macKeys';

import styles from './QuickSendSettings.module.css';
import type { ShortcutBlockProps } from './ShortcutBlock.types';
import { SwitchRow } from './SwitchRow';
import ui from './ui.module.css';

/** One global shortcut in the settings sheet: its switch, and while it is on a field that records the keys. */
export function ShortcutBlock({ label, hint, setting, problem: shellProblem, onSet, onRecording, testId }: ShortcutBlockProps): React.JSX.Element {
  const t = useT();
  const id = useId();
  const [recording, setRecording] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!recording) return;
    onRecording(true);
    // the sheet's own Escape and Enter must not see these keys: they are the shortcut now
    const onKey = (event: KeyboardEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      if (event.code === 'Escape') {
        setRecording(false);
        setProblem(null);
        return;
      }
      const recorded = recordShortcut(event);
      if (recorded === null) return;
      if ('problem' in recorded) {
        setProblem(recorded.problem);
        return;
      }
      setProblem(null);
      setRecording(false);
      onSet(true, recorded.shortcut);
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      onRecording(false);
    };
  }, [recording, onSet, onRecording]);

  const shown = problem ?? shellProblem;
  const chip = setting.enabled ? (
    <button
      type="button"
      className={[styles.recorder, shown === null ? '' : styles.recorderInvalid].join(' ')}
      aria-label={t('shortcutBlock.chipLabel', { label, shortcut: setting.shortcut.label })}
      aria-describedby={shown === null ? undefined : `${id}-problem`}
      onClick={() => {
        setProblem(null);
        setRecording((value) => !value);
      }}
      onBlur={() => {
        setRecording(false);
      }}
      data-recording={recording}
      data-testid={`${testId}-shortcut`}
    >
      {recording ? t('shortcutBlock.pressKeys') : setting.shortcut.label}
    </button>
  ) : null;
  return (
    <div className={styles.row}>
      <SwitchRow
        label={label}
        hint={hint}
        checked={setting.enabled}
        onChange={(on) => {
          onSet(on, setting.shortcut);
        }}
        control={chip}
        data-testid={`${testId}-switch`}
      />
      {recording && shown === null ? <p className={ui.fieldHint}>{t('shortcutBlock.escCancel')}</p> : null}
      {shown === null ? null : (
        <p id={`${id}-problem`} className={ui.fieldError} role="alert">
          {shown}
        </p>
      )}
    </div>
  );
}
